<template>
    <div class="saas-page linear-page">
        <section class="linear-hero">
            <div>
                <p class="linear-kicker">
                    <router-link to="/home">{{ trans('general.home') }}</router-link>
                    <span>/</span>
                    <span>{{ trans('user.profile') }}</span>
                </p>
                <h1>{{ trans('user.profile') }}</h1>
                <p class="linear-copy">Keep administrator identity, personal details, and public profile links current.</p>
            </div>
            <div class="linear-command-card">
                <span class="linear-dot"></span>
                <span>{{ trans('user.update_profile') }}</span>
                <kbd>{{ trans('general.save') }}</kbd>
            </div>
        </section>

        <div class="linear-grid">
            <section class="linear-card linear-card--wide">
                <div class="linear-card__head">
                    <div>
                        <span class="linear-label">{{ trans('user.profile') }}</span>
                        <h2>{{ trans('user.update_profile') }}</h2>
                    </div>
                    <span class="linear-status">{{ trans('general.save') }}</span>
                </div>
                <form @submit.prevent="updateProfile" @keydown="profileForm.errors.clear($event.target.name)">
                    <div class="linear-form-grid">
                        <div class="form-group">
                            <label>{{ trans('user.first_name') }}</label>
                            <input class="form-control linear-input" type="text" name="first_name" v-model="profileForm.first_name">
                            <show-error :form-name="profileForm" prop-name="first_name"></show-error>
                        </div>
                        <div class="form-group">
                            <label>{{ trans('user.last_name') }}</label>
                            <input class="form-control linear-input" type="text" name="last_name" v-model="profileForm.last_name">
                            <show-error :form-name="profileForm" prop-name="last_name"></show-error>
                        </div>
                        <div class="form-group">
                            <label>{{ trans('user.date_of_birth') }}</label>
                            <datepicker v-model="profileForm.date_of_birth" :bootstrapStyling="true" name="date_of_birth" @selected="profileForm.errors.clear('date_of_birth')"></datepicker>
                            <show-error :form-name="profileForm" prop-name="date_of_birth"></show-error>
                        </div>
                        <div class="form-group">
                            <label>{{ trans('user.date_of_anniversary') }}</label>
                            <datepicker v-model="profileForm.date_of_anniversary" :bootstrapStyling="true" name="date_of_anniversary" @selected="profileForm.errors.clear('date_of_anniversary')"></datepicker>
                            <show-error :form-name="profileForm" prop-name="date_of_anniversary"></show-error>
                        </div>
                    </div>
                    <div class="linear-section">
                        <label>{{ trans('user.gender') }}</label>
                        <div class="linear-radio-list">
                            <label class="linear-radio" v-for="gender in genders" :key="gender.id" :for="'profile-gender-' + gender.id">
                                <input
                                    type="radio"
                                    :value="gender.id"
                                    :id="'profile-gender-' + gender.id"
                                    v-model="profileForm.gender"
                                    :checked="profileForm.gender == gender.id"
                                    name="gender"
                                    @click="profileForm.errors.clear('gender')"
                                >
                                <span>{{ trans('list.' + gender.id) }}</span>
                            </label>
                        </div>
                        <show-error :form-name="profileForm" prop-name="gender"></show-error>
                    </div>
                    <div class="linear-section">
                        <div class="linear-card__head linear-card__head--sub">
                            <div>
                                <span class="linear-label">Social</span>
                                <h2>{{ trans('user.profile') }}</h2>
                            </div>
                        </div>
                        <div class="linear-form-grid">
                            <div class="form-group">
                                <label>{{ trans('user.sm_profile',{name: 'Facebook'}) }}</label>
                                <input class="form-control linear-input" type="text" name="facebook_profile" v-model="profileForm.facebook_profile">
                                <show-error :form-name="profileForm" prop-name="facebook_profile"></show-error>
                            </div>
                            <div class="form-group">
                                <label>{{ trans('user.sm_profile',{name: 'Twitter'}) }}</label>
                                <input class="form-control linear-input" type="text" name="twitter_profile" v-model="profileForm.twitter_profile">
                                <show-error :form-name="profileForm" prop-name="twitter_profile"></show-error>
                            </div>
                            <div class="form-group">
                                <label>{{ trans('user.sm_profile',{name: 'Google Plus'}) }}</label>
                                <input class="form-control linear-input" type="text" name="google_plus_profile" v-model="profileForm.google_plus_profile">
                                <show-error :form-name="profileForm" prop-name="google_plus_profile"></show-error>
                            </div>
                            <div class="form-group">
                                <label>{{ trans('user.sm_profile',{name: 'Linkedin'}) }}</label>
                                <input class="form-control linear-input" type="text" name="linkedin_profile" v-model="profileForm.linkedin_profile">
                                <show-error :form-name="profileForm" prop-name="linkedin_profile"></show-error>
                            </div>
                        </div>
                    </div>
                    <div class="linear-actions">
                        <button type="submit" class="btn btn-sm admin-toolbar-btn admin-toolbar-btn--solid">{{ trans('general.save') }}</button>
                    </div>
                </form>
            </section>

            <aside class="linear-card linear-avatar-card">
                <div class="linear-card__head text-center">
                    <div>
                        <span class="linear-label">{{ trans('user.profile') }}</span>
                        <h2>{{ trans('user.avatar') }}</h2>
                    </div>
                </div>
                <div class="linear-avatar-shell">
                    <upload-image
                        id="avatar"
                        presentation="avatar"
                        :upload-path="`/user/profile/avatar/${getAuthUser('id')}`"
                        :remove-path="`/user/profile/avatar/remove/${getAuthUser('id')}`"
                        :image-source="avatar.user"
                        @uploaded="updateAvatar"
                        @removed="updateAvatar"
                    ></upload-image>
                </div>
            </aside>
        </div>
    </div>
</template>

<script>
    import datepicker from 'vuejs-datepicker'
    import uploadImage from '../../components/upload-image'

    export default {
        components : { datepicker,uploadImage },
        data() {
            return {
                genders: [],
                profileForm: new Form({
                    first_name : '',
                    last_name : '',
                    date_of_birth : '',
                    date_of_anniversary : '',
                    gender : '',
                    facebook_profile : '',
                    twitter_profile : '',
                    google_plus_profile : '',
                    linkedin_profile : ''
                }, false),
                avatar: {
                    user: ''
                }
            };
        },
        mounted(){
            axios.get('/api/fetch/lists?lists=gender')
                .then(response => response.data)
                .then(response => {
                    this.genders = response.data.gender;
                })
                .catch(error => {
                    helper.showDataErrorMsg(error);
                });
            axios.get('/api/user/detail')
                .then(response => response.data)
                .then(response => {
                    this.profileForm.first_name = response.profile.first_name;
                    this.profileForm.last_name = response.profile.last_name;
                    this.profileForm.date_of_birth = response.profile.date_of_birth;
                    this.profileForm.date_of_anniversary = response.profile.date_of_anniversary;
                    this.profileForm.gender = response.profile.gender;
                    this.profileForm.facebook_profile = response.profile.facebook_profile;
                    this.profileForm.twitter_profile = response.profile.twitter_profile;
                    this.profileForm.google_plus_profile = response.profile.google_plus_profile;
                    this.profileForm.linkedin_profile = response.profile.linkedin_profile;
                    this.avatar.user = response.profile.avatar;
                })
                .catch(error => {
                    helper.showDataErrorMsg(error);
                });
        },
        methods: {
            updateProfile() {
                this.profileForm.date_of_birth = (this.profileForm.date_of_birth) ? moment(this.profileForm.date_of_birth).format('YYYY-MM-DD') : null;
                this.profileForm.date_of_anniversary = (this.profileForm.date_of_anniversary) ? moment(this.profileForm.date_of_anniversary).format('YYYY-MM-DD') : null;
                this.profileForm.post('/api/user/profile/update')
                    .then(response => {
                        toastr.success(response.message);
                        this.$store.dispatch('setAuthUserName',{
                            first_name: this.profileForm.first_name,
                            last_name: this.profileForm.last_name
                        });
                    }).catch(error => {
                        helper.showErrorMsg(error);
                    });
            },
            updateAvatar(val){
                this.$store.dispatch('setAuthUserDetail',{
                    avatar: val
                });
            },
            getAuthUser(name){
                return helper.getAuthUser(name);
            }
        },
        computed: {
        }
    }
</script>

<style scoped>
.linear-page {
    color: #111318;
}

.linear-hero {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(260px, 0.34fr);
    gap: 1.25rem;
    align-items: end;
    margin-bottom: 1rem;
}

.linear-kicker,
.linear-label {
    color: #625cf6;
    font-size: 0.6875rem;
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
}

.linear-kicker {
    display: flex;
    gap: 0.45rem;
    margin-bottom: 0.65rem;
}

.linear-hero h1 {
    margin: 0;
    font-size: clamp(2rem, 4vw, 3rem);
    font-weight: 800;
    letter-spacing: -0.055em;
}

.linear-copy {
    max-width: 42rem;
    margin: 0.75rem 0 0;
    color: #687083;
    line-height: 1.65;
}

.linear-command-card,
.linear-card {
    border: 1px solid #e5e8ef;
    border-radius: 18px;
    background: rgba(255, 255, 255, 0.9);
    box-shadow: 0 16px 48px rgba(31, 35, 48, 0.04), inset 0 1px 0 rgba(255, 255, 255, 0.86);
}

.linear-command-card {
    display: flex;
    align-items: center;
    gap: 0.65rem;
    min-height: 3.2rem;
    padding: 0.5rem 0.85rem;
    color: #687083;
    font-weight: 600;
}

.linear-command-card kbd {
    margin-left: auto;
    border: 1px solid #e5e8ef;
    border-radius: 7px;
    color: #8b93a7;
    background: #fff;
}

.linear-dot {
    width: 0.5rem;
    height: 0.5rem;
    border-radius: 999px;
    background: #625cf6;
    box-shadow: 0 0 0 4px rgba(98, 92, 246, 0.1);
}

.linear-grid {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 340px;
    gap: 1rem;
    align-items: start;
}

.linear-card {
    padding: 1.1rem;
}

.linear-card__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 1rem;
}

.linear-card__head--sub {
    margin-bottom: 0.85rem;
}

.linear-card__head h2 {
    margin: 0.35rem 0 0;
    font-size: 1rem;
    font-weight: 800;
    letter-spacing: -0.03em;
}

.linear-status {
    padding: 0.28rem 0.55rem;
    border: 1px solid rgba(98, 92, 246, 0.18);
    border-radius: 999px;
    color: #5147d9;
    font-size: 0.7rem;
    font-weight: 800;
    background: #eff0ff;
}

.linear-form-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.85rem;
}

.linear-page label {
    color: #485064;
    font-size: 0.78rem;
    font-weight: 700;
}

.linear-input,
.linear-page ::v-deep .form-control {
    min-height: 42px;
    border-color: #e5e8ef;
    border-radius: 12px;
    background: #fbfcff;
}

.linear-section {
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 1px solid #e5e8ef;
}

.linear-radio-list {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
}

.linear-radio {
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
    min-height: 2.25rem;
    margin: 0;
    padding: 0 0.7rem;
    border: 1px solid #e5e8ef;
    border-radius: 999px;
    background: #fff;
}

.linear-radio input {
    accent-color: #625cf6;
}

.linear-actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 1rem;
}

.linear-avatar-card {
    position: sticky;
    top: 5rem;
}

.linear-avatar-card .linear-card__head {
    justify-content: center;
}

.linear-avatar-shell {
    padding: 1rem;
    border: 1px solid #e5e8ef;
    border-radius: 16px;
    background: linear-gradient(180deg, #fbfcff, #f7f8fb);
}

@media (max-width: 991.98px) {
    .linear-hero,
    .linear-grid,
    .linear-form-grid {
        grid-template-columns: 1fr;
    }

    .linear-avatar-card {
        position: static;
    }
}
</style>
